<template>
  <div class="market-card">
    <div class="market-card__head">
      <div class="market-card__title">{{ $t('market-day-price') }}</div>
      <div
        class="market-card__status"
        :class="fetchingMarketPrices ? 'is-on' : 'is-off'"
      >
        <span class="market-card__dot" />
        {{ fetchingMarketPrices ? $t('online') : $t('offline') }}
      </div>
    </div>
    <div
      v-if="marketItems.length"
      class="market-card__list"
    >
      <div
        v-for="(item, index) in marketItems"
        :key="item.name || index"
        class="market-card__row"
      >
        <span class="market-card__asset">
          <span
            class="market-card__icon"
            :class="'is-' + assetKind(item.name)"
            aria-hidden="true"
          >
            <svg
              v-if="assetKind(item.name) === 'gold'"
              viewBox="0 0 16 16"
            >
              <rect x="2.2" y="7.2" width="11.6" height="5.2" rx="1.1" fill="#E0B43A"/>
              <rect x="3.4" y="4.4" width="9.2" height="3.6" rx="0.9" fill="#F5D15A"/>
              <path d="M3.8 6.1h8.4" stroke="#FFF6C8" stroke-width="0.7" stroke-linecap="round" opacity="0.85"/>
              <circle cx="11.6" cy="4.2" r="1.1" fill="#FFE38A"/>
            </svg>
            <svg
              v-else-if="assetKind(item.name) === 'coin'"
              viewBox="0 0 16 16"
            >
              <circle cx="8" cy="8" r="6.1" fill="#F0B429"/>
              <circle cx="8" cy="8" r="4.3" fill="none" stroke="#C48416" stroke-width="1"/>
              <path d="M8 5.6l.7 1.5 1.6.2-1.2 1.1.3 1.6L8 9.2l-1.4.8.3-1.6-1.2-1.1 1.6-.2z" fill="#8A4B0F"/>
              <circle cx="5.6" cy="5.4" r="1" fill="#FFF4C2" opacity="0.8"/>
            </svg>
            <svg
              v-else
              viewBox="0 0 16 16"
            >
              <circle cx="8" cy="8" r="6.1" fill="#22C55E"/>
              <text
                x="8"
                y="11.1"
                text-anchor="middle"
                fill="#fff"
                font-size="8.2"
                font-weight="700"
              >{{ fxMark(item.name) }}</text>
            </svg>
          </span>
          <span class="market-card__name">{{ $t(item.name) }}</span>
        </span>
        <span class="market-card__price">
          {{ $numberFormat(item.price) }}
          {{ $store.state.currencySymbol }}
        </span>
        <span
          class="market-card__trend"
          :class="trendClass(item)"
        >
          <v-icon
            v-if="hasChange(item) && item.diffTitle == 'high'"
            x-small
          >
            mdi-menu-up
          </v-icon>
          <v-icon
            v-else-if="hasChange(item) && item.diffTitle == 'low'"
            x-small
          >
            mdi-menu-down
          </v-icon>
          <span v-if="hasChange(item)">{{ item.percent }}٪</span>
          <span v-else>—</span>
        </span>
      </div>
    </div>
    <div
      v-else
      class="market-card__empty"
    >
      <v-progress-circular
        :width="3"
        color="blue"
        indeterminate
        size="22"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'MarketDayPrice',
  data () {
    return {
      marketItems: [],
      fetchingMarketPrices: false,
      intervalId: null
    }
  },
  mounted () {
    this.fetchMarketPrice()
    this.intervalId = setInterval(() => {
      this.fetchMarketPrice()
    }, 60000)
  },
  beforeDestroy () {
    clearInterval(this.intervalId)
  },
  methods: {
    assetKind (name) {
      if (String(name).indexOf('arz_') === 0) return 'fx'
      if (String(name).indexOf('sekke_') === 0) return 'coin'
      return 'gold'
    },
    fxMark (name) {
      if (name === 'arz_euro') return '€'
      if (name === 'arz_derham') return 'د'
      return '$'
    },
    hasChange (item) {
      return item.percent != 0 && item.percent != null && item.percent !== ''
    },
    trendClass (item) {
      if (!this.hasChange(item)) return 'is-flat'
      if (item.diffTitle == 'high') return 'is-up'
      if (item.diffTitle == 'low') return 'is-down'
      return 'is-flat'
    },
    fetchMarketPrice () {
      this.$axios.get(this.$endpoint + 'gold-prices')
        .then(r => {
          this.marketItems = Object.entries(r.data.data).map(([key, value]) => ({
            name: key,
            ...value
          }))
          this.fetchingMarketPrices = true
        })
        .catch(() => {
          this.fetchingMarketPrices = false
        })
    }
  }
}
</script>

<style scoped>
.market-card {
  width: 100%;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  max-height: 280px;
  min-width: 260px;
  overflow: hidden;
}
.market-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}
.market-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.market-card__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  white-space: nowrap;
}
.market-card__status.is-on {
  color: #00A63E;
}
.market-card__status.is-off {
  color: #E7000B;
}
.market-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.market-card__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.market-card__list::-webkit-scrollbar {
  width: 4px;
}
.market-card__list::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 4px;
}
.market-card__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #F3F4F6;
  min-height: 34px;
}
.market-card__row:last-child {
  border-bottom: none;
}
.market-card__asset {
  flex: 1 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.market-card__icon {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}
.market-card__icon svg {
  width: 14px;
  height: 14px;
  display: block;
}
.market-card__icon.is-gold {
  background: linear-gradient(180deg, #FFF6D2 0%, #F3E0A4 100%);
}
.market-card__icon.is-coin {
  background: linear-gradient(180deg, #FFE7C2 0%, #F5C98A 100%);
}
.market-card__icon.is-fx {
  background: linear-gradient(180deg, #E9FBE8 0%, #C8F0D2 100%);
}
.market-card__name {
  min-width: 0;
  font-size: 12px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.market-card__price {
  flex: 0 0 auto;
  font-size: 12px;
  color: #374151;
  white-space: nowrap;
}
.market-card__trend {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  min-width: 48px;
  font-size: 11px;
  white-space: nowrap;
}
.market-card__trend.is-up {
  color: #00A63E;
}
.market-card__trend.is-down {
  color: #E7000B;
}
.market-card__trend.is-flat {
  color: #9CA3AF;
}
.market-card__trend .v-icon {
  color: inherit !important;
}
.market-card__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}
</style>
